<script setup>
/**
 * The Mystic's side: Found in search (lesson-redo DESIGN.md G; API.md §5).
 *
 * ✦ FOUND IN SEARCH · This week. "You came up in 34 searches. 6 people
 * opened your page." (the numbers count up once; reduced motion shows them).
 * What people searched: the top three phrases (3 or more searches only; no
 * one's search on its own). Be found more: Your words → Edit, What you read
 * for → Change (up to three of the seven topics), Your lessons, Your voice →
 * Open. Then how this Mystic appears in a search, from the real result card.
 *
 * Emits `navigate` ('identity' | 'voice') for the Studio to switch tabs.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { getFoundInSearch, setReadForTopics } from '@/api-service'
import MysticResult from '@/components/ui/community/MysticResult.vue'

const emit = defineEmits(['navigate'])
const MAX = 3

const data = ref(null)
const shown = ref(0)
const opened = ref(0)
const picking = ref(false)
const picked = ref([])
const saving = ref(false)
const failed = ref('')

const topics = computed(() => (data.value && Array.isArray(data.value.topics) ? data.value.topics : []))
const readFor = computed(() => (data.value && Array.isArray(data.value.read_for_topics) ? data.value.read_for_topics : []))
const phrases = computed(() => (data.value && Array.isArray(data.value.phrases) ? data.value.phrases.slice(0, 3) : []))
const preview = computed(() => (data.value && data.value.preview) || null)
const week = computed(() => (data.value && data.value.week) || { shown: 0, opened: 0 })
const labelOf = (k) => (topics.value.find((t) => t.key === k) || {}).label || k
function listWords(keys) {
  const ls = keys.map(labelOf)
  if (!ls.length) return 'Nothing picked yet.'
  if (ls.length === 1) return `${ls[0]}.`
  return `${ls.slice(0, -1).join(', ')} and ${ls[ls.length - 1]}.`
}
const readForNow = computed(() => listWords(readFor.value))

const reduced = () => typeof window !== 'undefined' && !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
let raf = 0
function countUp(toShown, toOpened) {
  if (reduced() || typeof window === 'undefined' || !window.requestAnimationFrame) { shown.value = toShown; opened.value = toOpened; return }
  const t0 = performance.now()
  const step = (t) => {
    const p = Math.min(1, (t - t0) / 900)
    const e = 1 - Math.pow(1 - p, 3)
    shown.value = Math.round(toShown * e)
    opened.value = Math.round(toOpened * e)
    if (p < 1) raf = window.requestAnimationFrame(step)
  }
  raf = window.requestAnimationFrame(step)
}

function startPick() { picked.value = [...readFor.value]; failed.value = ''; picking.value = true }
function toggle(k) {
  const i = picked.value.indexOf(k)
  if (i >= 0) picked.value = picked.value.filter((x) => x !== k)
  else if (picked.value.length < MAX) picked.value = [...picked.value, k]
}
async function save() {
  if (saving.value) return
  saving.value = true
  failed.value = ''
  const r = await setReadForTopics(picked.value)
  saving.value = false
  if (r && Array.isArray(r.read_for_topics)) {
    data.value = { ...data.value, read_for_topics: r.read_for_topics }
    picking.value = false
    // The preview is the server's search for what they read for: fetch it
    // again so it shows the new pick (lesson walk B11). No second count-up.
    const d = await getFoundInSearch()
    if (d) data.value = { ...d, read_for_topics: r.read_for_topics }
  } else {
    failed.value = 'That didn’t save. Try once more.'
  }
}

onMounted(async () => {
  const d = await getFoundInSearch()
  if (!d) return
  data.value = d
  countUp(Number(d.week && d.week.shown) || 0, Number(d.week && d.week.opened) || 0)
})
onBeforeUnmount(() => { if (raf && typeof window !== 'undefined') window.cancelAnimationFrame(raf) })
</script>

<template>
  <section v-if="data" class="fs" aria-label="Found in search" data-testid="found-in-search">
    <p class="fs-h">✦ Found in search <span>This week</span></p>
    <p class="fs-big">You came up in <b>{{ shown }}</b> search{{ week.shown === 1 ? '' : 'es' }}. <b>{{ opened }}</b> {{ week.opened === 1 ? 'person' : 'people' }} opened your page.</p>

    <template v-if="phrases.length">
      <p class="fs-sub">What people searched</p>
      <ul class="fs-q">
        <li v-for="p in phrases" :key="p.phrase"><span>{{ p.phrase }}</span><span>{{ p.count }}</span></li>
      </ul>
    </template>

    <p class="fs-sub">Be found more</p>
    <ul class="fs-do">
      <li>
        <b>Your words</b><p>Search reads your description. Say plainly what you read for.</p>
        <button type="button" data-testid="fs-words" @click="emit('navigate', 'identity')">Edit</button>
      </li>
      <li>
        <b>What you read for</b>
        <p><span data-testid="fs-read-for-now">{{ readForNow }}</span> Pick up to three topics; searches on them bring you up.</p>
        <button v-if="!picking" type="button" data-testid="fs-read-for" @click="startPick">Change</button>
        <div v-if="picking" class="fs-pick" role="group" aria-label="What you read for, up to three">
          <button v-for="t in topics" :key="t.key" type="button" class="fs-topic" :class="{ on: picked.includes(t.key) }"
                  :aria-pressed="picked.includes(t.key) ? 'true' : 'false'"
                  :disabled="!picked.includes(t.key) && picked.length >= MAX" data-testid="fs-topic" @click="toggle(t.key)">{{ t.label }}</button>
          <span class="fs-pick-do">
            <button type="button" class="fs-save" :disabled="saving" data-testid="fs-read-for-save" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
            <button type="button" class="fs-cancel" @click="picking = false">Cancel</button>
          </span>
          <span v-if="failed" class="fs-err" role="alert">{{ failed }}</span>
        </div>
      </li>
      <li>
        <b>Your lessons</b><p>When a seeker shares a reading with you, its lesson carries your name into results.</p>
      </li>
      <li>
        <b>Your voice</b><p>Lines in your Voice tab are searched too.</p>
        <button type="button" data-testid="fs-voice" @click="emit('navigate', 'voice')">Open</button>
      </li>
    </ul>
    <p class="fs-note">Only searches made 3 or more times show here. Nobody’s search is shown to you on its own.</p>

    <div v-if="preview && preview.mystic" class="fs-pv">
      <p>How you appear in a search for “{{ preview.q || 'love' }}”</p>
      <MysticResult :mystic="preview.mystic" />
    </div>
  </section>
</template>

<style scoped>
.fs {
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif; --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  max-width: 620px; margin: 0 0 20px; border-radius: 20px; padding: 22px 24px 18px; box-sizing: border-box; color: #ece2fb; font-family: var(--ui);
  background: linear-gradient(180deg, rgba(255, 255, 255, .06), rgba(255, 255, 255, .03)); border: 1px solid rgba(231, 201, 131, .3); box-shadow: 0 24px 50px -30px #000;
}
.fs *, .fs *::before, .fs *::after { box-sizing: border-box; }
.fs-h { display: flex; justify-content: space-between; align-items: baseline; margin: 0 0 12px; font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: #e7c983; }
.fs-h span { font: 500 12px var(--ui); letter-spacing: 0; text-transform: none; color: #a996c9; }
.fs-big { margin: 0; font: 600 27px/1.25 var(--serif); color: #fff6e4; }
.fs-big b { color: #ffe9b8; font-variant-numeric: tabular-nums; }
.fs-sub { margin: 18px 0 6px; font: 600 9.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: #cdb98a; }
.fs-q { margin: 0; padding: 0; list-style: none; }
.fs-q li { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid rgba(185, 166, 214, .1); font: 500 15px var(--ui); color: #ece2fb; }
.fs-q li span:last-child { color: #a996c9; font-variant-numeric: tabular-nums; }
.fs-do { margin: 0; padding: 0; list-style: none; }
.fs-do li { display: grid; grid-template-columns: 1fr auto; gap: 4px 16px; align-items: center; padding: 10px 0; border-bottom: 1px solid rgba(185, 166, 214, .1); }
.fs-do li:last-child { border-bottom: 0; }
.fs-do b { font: 600 15px var(--ui); color: #fff; }
.fs-do p { grid-column: 1; margin: 0; font: 500 13.5px/1.45 var(--ui); color: #b9a6d6; }
.fs-do li > button { grid-column: 2; grid-row: 1 / span 2; min-height: 44px; padding: 0 4px; border: 0; background: none; cursor: pointer; color: #f0d9a3;
  font: 600 13.5px var(--ui); text-decoration: underline; text-underline-offset: 3px; }
.fs-do li > button:focus-visible, .fs-topic:focus-visible, .fs-save:focus-visible, .fs-cancel:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }
/* The picker: tools, so small toggles (chips are tools). */
.fs-pick { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.fs-topic { min-height: 40px; padding: 0 14px; border-radius: 999px; border: 1px solid rgba(195, 169, 255, .3); background: transparent; color: #ece2fb;
  font: 600 13px var(--ui); cursor: pointer; }
.fs-topic.on { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; border-color: rgba(240, 217, 163, .55); }
.fs-topic:disabled { opacity: .45; cursor: default; }
.fs-pick-do { display: flex; gap: 12px; width: 100%; margin-top: 4px; }
.fs-save { min-height: 40px; padding: 0 18px; border-radius: 999px; border: 1px solid rgba(240, 217, 163, .55); background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #ffdfa3; font: 700 13px var(--ui); cursor: pointer; }
.fs-cancel { min-height: 40px; border: 0; background: none; color: #b9a6d6; font: 500 13px var(--ui); cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.fs-err { width: 100%; font: 500 13px var(--ui); color: #f3a6a6; }
.fs-note { margin: 14px 0 0; font: 500 12px/1.5 var(--ui); color: #8e7fae; }
.fs-pv { margin-top: 22px; }
.fs-pv p { margin: 0 0 8px; font: 600 9.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: #cdb98a; }
@media (max-width: 480px) { .fs { padding: 18px 16px 14px; } .fs-big { font-size: 23px; } }
</style>
